import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { api } from '../lib/api'
import { useAuthStore } from '../stores/auth'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Pagination } from '../components/ui/Pagination'
import { useClientPagination } from '../hooks/useClientPagination'

type AlertType =
  | 'shopify_vip_dormant'
  | 'shopify_recurrent_dormant'
  | 'contract_expiring'
  | 'lab_expiring'
  | 'task_overdue'
  | 'meeting_upcoming'
  | 'anaf_invoice_due'
  | 'stoc_pe_terminate'
  | 'ocr_bonuri_cazut'
  | 'decont_nedepus'
  | 'expunere_expira'
  | 'act_masina_expira'
  | 'colet_intarziat'
  | 'incasari_curier'

type Severity = 'high' | 'medium' | 'low'

interface Alert {
  id: string
  type: AlertType
  severity: Severity
  title: string
  description: string
  link: string
  entityType: string
  entityId: number
  daysUntil: number
  value?: string
  hasTask: boolean
}

const TYPE_LABELS: Record<AlertType, string> = {
  shopify_vip_dormant: 'VIP inactiv',
  shopify_recurrent_dormant: 'Recurent în reactivare',
  contract_expiring: 'Contract expiră',
  lab_expiring: 'Lot expiră',
  task_overdue: 'Task depășit',
  meeting_upcoming: 'Întâlnire',
  anaf_invoice_due: 'Factură scadentă',
  stoc_pe_terminate: 'Stoc pe terminate',
  ocr_bonuri_cazut: 'Citirea bonurilor',
  decont_nedepus: 'Decont nedepus',
  expunere_expira: 'Expunere expiră',
  act_masina_expira: 'ITP / RCA / rovinietă',
  colet_intarziat: 'Colet întârziat',
  incasari_curier: 'Încasări curieri',
}

const SEVERITY_COLORS: Record<Severity, string> = {
  high: 'bg-b4l-red-light text-b4l-red-dark border-b4l-red/30',
  medium: 'bg-b4l-amber-light text-b4l-amber-dark border-b4l-amber/30',
  low: 'bg-b4l-blue-light text-b4l-blue-dark border-b4l-blue/30',
}

const SEVERITY_LABELS: Record<Severity, string> = {
  high: 'Critic',
  medium: 'Important',
  low: 'Info',
}

export function AlertePage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const navigate = useNavigate()
  const canWrite = permissions.includes('alerts:write')

  const [typeFilter, setTypeFilter] = useState<AlertType | ''>('')
  const [severityFilter, setSeverityFilter] = useState<Severity | ''>('')
  const [hideWithTask, setHideWithTask] = useState(false)

  const query = useQuery<{ alerts: Alert[] }>({
    queryKey: ['alerts'],
    queryFn: async () => (await api.get<{ alerts: Alert[] }>('/alerts')).data,
    refetchInterval: 5 * 60_000,
  })

  const createTask = useMutation({
    mutationFn: async (alertId: string) =>
      (
        await api.post<{ task: { id: number }; dedupe: boolean; poateDeschide: boolean }>(
          '/alerts/create-task',
          { alertId },
        )
      ).data,
    // Task-ul intra la „De preluat" in departamentul care se ocupa de acest fel de alerta
    // (12.10.2026) — nu la cine a apasat. Il ia cine are timp de acolo.
    onSuccess: (d) => {
      const deschide = d.poateDeschide
        ? { action: { label: 'Deschide', onClick: () => navigate(`/task-uri?view=${d.task.id}`) } }
        : {}
      if (d.dedupe)
        toast.info(
          d.poateDeschide
            ? 'Există deja un task deschis pentru această alertă.'
            : 'Există deja un task deschis pentru această alertă, la alt departament.',
          deschide,
        )
      else toast.success('Task creat, la „De preluat” în departamentul care se ocupă.', deschide)
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
      queryClient.invalidateQueries({ queryKey: ['taskuri'] })
    },
    onError: (e: unknown) =>
      toast.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Task-ul nu s-a putut crea.',
      ),
  })

  const filtered = useMemo(() => {
    const all = query.data?.alerts ?? []
    return all.filter((a) => {
      if (typeFilter && a.type !== typeFilter) return false
      if (severityFilter && a.severity !== severityFilter) return false
      if (hideWithTask && a.hasTask) return false
      return true
    })
  }, [query.data, typeFilter, severityFilter, hideWithTask])
  const pag = useClientPagination(filtered, 10)

  const counts = useMemo(() => {
    const all = query.data?.alerts ?? []
    return {
      total: all.length,
      high: all.filter((a) => a.severity === 'high').length,
      medium: all.filter((a) => a.severity === 'medium').length,
      low: all.filter((a) => a.severity === 'low').length,
      byType: all.reduce(
        (acc, a) => {
          acc[a.type] = (acc[a.type] ?? 0) + 1
          return acc
        },
        {} as Record<string, number>,
      ),
    }
  }, [query.data])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Centru de alerte</h1>
        <div className="text-xs text-b4l-text-muted">
          Se actualizează automat la 5 minute · {counts.total} alerte active
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <SeverityCard
          label="Critic"
          count={counts.high}
          color="red"
          active={severityFilter === 'high'}
          onClick={() => setSeverityFilter(severityFilter === 'high' ? '' : 'high')}
        />
        <SeverityCard
          label="Important"
          count={counts.medium}
          color="amber"
          active={severityFilter === 'medium'}
          onClick={() => setSeverityFilter(severityFilter === 'medium' ? '' : 'medium')}
        />
        <SeverityCard
          label="Info"
          count={counts.low}
          color="blue"
          active={severityFilter === 'low'}
          onClick={() => setSeverityFilter(severityFilter === 'low' ? '' : 'low')}
        />
        <SeverityCard
          label="Total"
          count={counts.total}
          color="green"
          active={severityFilter === ''}
          onClick={() => setSeverityFilter('')}
        />
      </div>

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-b4l-text-dim">Tip alertă</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as AlertType | '')}
              className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
            >
              <option value="">Toate tipurile ({counts.total})</option>
              {(Object.keys(TYPE_LABELS) as AlertType[]).map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]} ({counts.byType[t] ?? 0})
                </option>
              ))}
            </select>
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={hideWithTask}
              onChange={(e) => setHideWithTask(e.target.checked)}
            />
            Ascunde alerte cu task creat
          </label>
        </div>
      </Card>

      <Card>
        {query.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă...</p>}
        {filtered.length === 0 && !query.isLoading && (
          <p className="text-sm text-b4l-text-muted">
            Nicio alertă activă pentru filtrele curente. 🎉
          </p>
        )}
        <ul className="space-y-1">
          {pag.paginated.map((a) => (
            <li
              key={a.id}
              className={`flex items-start gap-3 rounded border ${SEVERITY_COLORS[a.severity]} px-3 py-2 text-sm`}
            >
              <span className="mt-0.5 inline-block whitespace-nowrap rounded bg-white/60 px-1.5 py-0.5 text-[10px] font-bold uppercase">
                {SEVERITY_LABELS[a.severity]}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{a.title}</div>
                <div className="text-xs opacity-80">{a.description}</div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                  <span className="rounded bg-white/60 px-1.5 py-0.5 font-mono">
                    {TYPE_LABELS[a.type]}
                  </span>
                  <Link to={a.link} className="underline opacity-80 hover:opacity-100">
                    Vezi →
                  </Link>
                </div>
              </div>
              {/* Alerta despre un task depasit n-are „+ Task": ar face un task despre un task. */}
              {canWrite && a.type !== 'task_overdue' && (
                <div className="shrink-0">
                  {a.hasTask ? (
                    <span className="text-xs italic opacity-70">✓ Task creat</span>
                  ) : (
                    <Button
                      variant="secondary"
                      onClick={() => createTask.mutate(a.id)}
                      loading={createTask.isPending && createTask.variables === a.id}
                    >
                      + Task
                    </Button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
        <div className="mt-3">
          <Pagination
            page={pag.page}
            pageSize={pag.pageSize}
            total={pag.total}
            onPageChange={pag.setPage}
            onPageSizeChange={pag.setPageSize}
            itemLabel="alerte"
          />
        </div>
      </Card>
    </div>
  )
}

function SeverityCard({
  label,
  count,
  color,
  active,
  onClick,
}: {
  label: string
  count: number
  color: 'red' | 'amber' | 'blue' | 'green'
  active: boolean
  onClick: () => void
}) {
  const colors: Record<string, string> = {
    red: 'border-b4l-red/30 bg-b4l-red-light',
    amber: 'border-b4l-amber/30 bg-b4l-amber-light',
    blue: 'border-b4l-blue/30 bg-b4l-blue-light',
    green: 'border-b4l-green/30 bg-b4l-green-light',
  }
  const valueColor: Record<string, string> = {
    red: 'text-b4l-red-dark',
    amber: 'text-b4l-amber-dark',
    blue: 'text-b4l-blue-dark',
    green: 'text-b4l-green-dark',
  }
  return (
    <button
      onClick={onClick}
      className={`rounded-lg border p-4 text-left transition ${colors[color]} ${
        active ? 'ring-2 ring-offset-1 ring-b4l-text/30' : ''
      }`}
    >
      <div className={`text-xs font-medium uppercase ${valueColor[color]}`}>{label}</div>
      <div className={`mt-1 text-3xl font-semibold ${valueColor[color]}`}>{count}</div>
    </button>
  )
}
